import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight, Database, Bot, Layers, Icon } from "lucide-react"
import { api } from '@/lib/api'
import { seo } from '@shared/seo.config'
import { Seo } from '@/components/Seo'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { db, seedMock } from '@/lib/db'
import ProductCard from '@/components/ProductCard'

const pillars = [
  {
    icon: Layers,
    title: 'Một mã nguồn',
    body: 'Client trong src/, server trong server/. Type của database chảy thẳng ra component — đổi một cột là TypeScript báo lỗi ngay tại chỗ dùng.',
  },
  {
    icon: Database,
    title: 'Database của bạn',
    body: 'Đổi đúng một dòng DATABASE_URL là đổi cả hệ thống lưu trữ. Postgres nào cũng chạy: VPS tự dựng, Neon, RDS. Không khoá vào nhà cung cấp nào.',
  },
  {
    icon: Bot,
    title: 'AI đọc được',
    body: 'robots.txt, sitemap.xml và llms.txt sinh tự động. Worker trả HTML có nội dung cho crawler của ChatGPT, Claude, Perplexity — thứ SPA thường vô hình với họ.',
  },
]

export default function Home() {
  const health = useQuery({ queryKey: ['health'], queryFn: api.health })
  const connected = health.data?.database === 'configured'
  const [products, setProducts] = useState<any[] | null>(null)

  useEffect(() => {
    seedMock('products', [
      { id: 'p1', name: 'Máy hút sữa điện đôi không đau', price: 1290000, old_price: 1590000, image: 'https://images.unsplash.com/photo-1584735935682-2f2b69dff9d2?w=1200&q=80', category: 'Mẹ', rating: 4.9, stock: 15, created_at: '2025-01-01T00:00:00.000Z' },
      { id: 'p2', name: 'Xe đẩy em bé gấp gọn 1 chiều', price: 2450000, old_price: 2990000, image: 'https://images.unsplash.com/photo-1591604032167-6e0e6b0f7d0d?w=1200&q=80', category: 'Bé', rating: 4.8, stock: 8, created_at: '2025-01-02T00:00:00.000Z' },
      { id: 'p3', name: 'Bộ quần áo sơ sinh cotton hữu cơ', price: 390000, old_price: null, image: 'https://images.unsplash.com/photo-1519689680058-324335c77eba?w=1200&q=80', category: 'Thời trang', rating: 4.7, stock: 30, created_at: '2025-01-03T00:00:00.000Z' },
      { id: 'p4', name: 'Ghế ăn dặm đa năng', price: 1890000, old_price: null, image: 'https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=1200&q=80', category: 'Ăn dặm', rating: 4.6, stock: 12, created_at: '2025-01-04T00:00:00.000Z' },
      { id: 'p5', name: 'Tã dán sơ sinh cao cấp 60 miếng', price: 285000, old_price: 320000, image: 'https://images.unsplash.com/photo-1600891964599-f61ba0e24092?w=1200&q=80', category: 'Tã bỉm', rating: 4.8, stock: 50, created_at: '2025-01-05T00:00:00.000Z' },
      { id: 'p6', name: 'Máy tiệt trùng bình sữa UV', price: 2450000, old_price: null, image: 'https://images.unsplash.com/photo-1585338107529-13afc5f02586?w=1200&q=80', category: 'Mẹ', rating: 4.9, stock: 6, created_at: '2025-01-06T00:00:00.000Z' },
    ])
    db.select('products').then(({ data }) => setProducts(data ?? []))
  }, [])

  return (
    <>
      <Seo />

      {/* Hero */}
      <section data-upbo-src="src/pages/Home.tsx:54" className="relative overflow-hidden border-b border-border/60">
        <div data-upbo-src="src/pages/Home.tsx:55"
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.035] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]"
        />
        <div data-upbo-src="src/pages/Home.tsx:59" className="relative mx-auto w-full max-w-6xl px-6 pt-24 pb-20">
          <StatusPill loading={health.isLoading} connected={connected} />

          <h1 data-upbo-src="src/pages/Home.tsx:62" className="mt-8 max-w-3xl text-balance text-5xl font-semibold leading-[1.05] tracking-[-0.03em] sm:text-6xl">
            {seo.siteName}
          </h1>
          <p data-upbo-src="src/pages/Home.tsx:65" className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            {seo.description}
          </p>

          <div data-upbo-src="src/pages/Home.tsx:69" className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-xs text-muted-foreground">
            <span data-upbo-src="src/pages/Home.tsx:70">src/ — giao diện</span>
            <span data-upbo-src="src/pages/Home.tsx:71" className="text-border">/</span>
            <span data-upbo-src="src/pages/Home.tsx:72">server/ — API + database</span>
            <span data-upbo-src="src/pages/Home.tsx:73" className="text-border">/</span>
            <span data-upbo-src="src/pages/Home.tsx:74">shared/ — dùng chung</span>
          </div>
        </div>
      </section>

      {/* Sản phẩm nổi bật */}
      <section data-upbo-src="src/pages/Home.tsx:80" className="relative overflow-hidden py-20">
        <div data-upbo-src="src/pages/Home.tsx:81" className="mx-auto w-full max-w-6xl px-6">
          <div data-upbo-src="src/pages/Home.tsx:82" className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            <div data-upbo-src="src/pages/Home.tsx:83" className="lg:col-span-4">
              <p data-upbo-src="src/pages/Home.tsx:84" className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Sản phẩm nổi bật</p>
              <h2 data-upbo-src="src/pages/Home.tsx:85" className="mt-3 text-3xl font-semibold tracking-tight">Đồ dùng mẹ & bé được yêu thích nhất</h2>
              <p data-upbo-src="src/pages/Home.tsx:86" className="mt-3 text-sm leading-relaxed text-muted-foreground">Chọn lọc từ những thương hiệu uy tín, an toàn cho làn da nhạy cảm và từng giai đoạn phát triển của bé.</p>
              <a data-upbo-src="src/pages/Home.tsx:87" href="/products" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                Xem tất cả sản phẩm
                <ArrowUpRight className="size-3.5" />
              </a>
            </div>
            <div data-upbo-src="src/pages/Home.tsx:92" className="lg:col-span-8">
              {products === null ? (
                <div data-upbo-src="src/pages/Home.tsx:94" className="grid gap-4 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-64 w-full rounded-xl" />
                  ))}
                </div>
              ) : (
                <div data-upbo-src="src/pages/Home.tsx:100" className="grid gap-4 sm:grid-cols-2">
                  {products.slice(0, 4).map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Ba trụ cột */}
      <section data-upbo-src="src/pages/Home.tsx:112" className="mx-auto w-full max-w-6xl px-6 py-20">
        <div data-upbo-src="src/pages/Home.tsx:113" className="grid gap-px overflow-hidden rounded-xl border bg-border sm:grid-cols-3">
          {pillars.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="rounded-none border-0 bg-background shadow-none">
              <CardContent className="flex flex-col gap-3 py-2">
                <Icon className="size-5 text-muted-foreground" strokeWidth={1.5} />
                <h2 data-upbo-src="src/pages/Home.tsx:118" className="font-medium tracking-tight">{title}</h2>
                <p data-upbo-src="src/pages/Home.tsx:119" className="text-sm leading-relaxed text-muted-foreground">{body}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <a data-upbo-src="src/pages/Home.tsx:125"
          href="/llms.txt"
          className="group mt-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          Xem llms.txt mà AI đọc được
          <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </section>
    </>
  )
}

/** Trạng thái database — thứ đầu tiên người dùng cần biết khi mở dự án lần đầu. */
function StatusPill({ loading, connected }: { loading: boolean; connected: boolean }) {
  if (loading) return <Skeleton className="h-6 w-44" />
  return (
    <Badge variant="outline" className="gap-2 px-3 py-1 font-normal">
      <span data-upbo-src="src/pages/Home.tsx:142"
        className={cn(
          'size-1.5 rounded-full',
          connected ? 'bg-emerald-500' : 'bg-amber-500',
        )}
      />
      {connected ? 'Đã kết nối database' : 'Chưa đặt DATABASE_URL'}
    </Badge>
  )
}
